/**
 * The skeleton on a pogo stick — one factory, used identically by the local
 * player and by every remote ghost.
 *
 * ~30 meshes built from **four module-level shared geometries** (box, sphere,
 * cylinder, cone). Geometries are shared across every rig in the scene and are
 * never disposed; materials are cloned per rig, because each player needs its
 * own hue and its own opacity for the ghost fade.
 *
 * The hierarchy is a real joint tree — `root → body → hips → spine → chest →
 * neck → skull`, shoulders and hips hanging off it, plus a separate stick group
 * — so `animation.ts` rotates *joints*, never meshes. Every pivot sits at
 * identity in its rest pose, and `applySkeletonPose` authors absolute angles.
 *
 * Stick-local Y is shared with the physics: the body origin is the collision
 * sphere centre, and the cone tip's point lands exactly at
 * `−(STICK_LENGTH + TIP_RADIUS)`, which is where `pogo.ts` puts the contact.
 *
 * The rig faces **local −Z** (three.js forward): eyes, jaw and toes all sit at
 * negative Z. The yaw `animation.ts` applies maps −Z to the camera's view
 * direction, so the player sees the skeleton's back, not its face.
 */

import {
  BoxGeometry,
  CanvasTexture,
  Color,
  CylinderGeometry,
  Group,
  LinearFilter,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  type BufferGeometry,
  type Texture,
} from 'three';
import { BONE_COLOR } from '@shared/constants';
import { TUNING } from '../physics/pogo';
import { applySkeletonPose, createAnimState, RIG, type SkeletonParts } from './animation';
import type { SkeletonPose, SkeletonRig } from './rigTypes';

// --- shared geometries (never disposed) ------------------------------------

/** Unit cube, 1 × 1 × 1. Scale it to the box you want. */
const SHARED_BOX = new BoxGeometry(1, 1, 1);
/** Unit-diameter sphere: `scale` is the diameter on each axis. */
const SHARED_SPHERE = new SphereGeometry(0.5, 10, 8);
/** Unit-diameter, unit-height cylinder along +Y. */
const SHARED_CYL = new CylinderGeometry(0.5, 0.5, 1, 8);
/** Unit cone: wide at +Y, a point at −Y — the stick tip, already facing down. */
const SHARED_CONE = new CylinderGeometry(0.5, 0.02, 1, 10);

// --- material templates (cloned per rig) -----------------------------------

const BONE_TEMPLATE = new MeshLambertMaterial({ color: 0xffffff, flatShading: true });
const STICK_TEMPLATE = new MeshLambertMaterial({ color: 0xffffff, flatShading: true });
const DARK_TEMPLATE = new MeshLambertMaterial({ color: 0x1b1d24, flatShading: true });
const SPRING_TEMPLATE = new MeshLambertMaterial({ color: 0xffffff, flatShading: true });

/** How far the bone colour is pulled toward the player's hue. */
const TINT_MIX = 0.45;

/** Height of the name tag above the body origin, metres. */
const TAG_HEIGHT = 2.2;

function addMesh(
  parent: Object3D,
  geo: BufferGeometry,
  mat: MeshLambertMaterial,
  x: number, y: number, z: number,
  sx: number, sy: number, sz: number,
): Mesh {
  const m = new Mesh(geo, mat);
  m.position.set(x, y, z);
  m.scale.set(sx, sy, sz);
  m.castShadow = true;
  parent.add(m);
  return m;
}

function joint(parent: Object3D, x: number, y: number, z: number): Object3D {
  const g = new Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
}

/**
 * Build one arm, from the shoulder pivot down to the hand.
 *
 * @param side `+1` for the rig's left (world +X in rest pose), `−1` for right.
 */
function buildArm(chest: Object3D, bone: MeshLambertMaterial, side: number): { shoulder: Object3D; elbow: Object3D } {
  const shoulder = joint(chest, 0.155 * side, 0.26, 0);
  addMesh(shoulder, SHARED_CYL, bone, 0, -0.14, 0, 0.07, 0.28, 0.07);
  const elbow = joint(shoulder, 0, -0.28, 0);
  addMesh(elbow, SHARED_CYL, bone, 0, -0.13, 0, 0.06, 0.26, 0.06);
  addMesh(elbow, SHARED_BOX, bone, 0, -0.30, -0.01, 0.085, 0.1, 0.07);
  return { shoulder, elbow };
}

/** Build one leg, from the hip pivot down to the foot. */
function buildLeg(hips: Object3D, bone: MeshLambertMaterial, side: number): {
  hip: Object3D;
  knee: Object3D;
  ankle: Object3D;
} {
  const hip = joint(hips, 0.105 * side, -0.06, 0);
  addMesh(hip, SHARED_CYL, bone, 0, -0.17, 0, 0.1, 0.34, 0.1);
  const knee = joint(hip, 0, -0.34, 0);
  addMesh(knee, SHARED_CYL, bone, 0, -0.15, 0, 0.08, 0.3, 0.08);
  const ankle = joint(knee, 0, -0.3, 0);
  addMesh(ankle, SHARED_BOX, bone, 0, -0.03, -0.045, 0.11, 0.06, 0.22);
  return { hip, knee, ankle };
}

/**
 * Create a skeleton rig tinted with a player's hue.
 *
 * @param tint `0xRRGGBB` from `PALETTE`.
 */
export function createSkeleton(tint: number): SkeletonRig {
  const tintColor = new Color(tint);

  // Bone stays readable as bone; the hue is what tells two players apart.
  const bone = BONE_TEMPLATE.clone();
  bone.color.set(BONE_COLOR).lerp(tintColor, TINT_MIX);
  const stick = STICK_TEMPLATE.clone();
  stick.color.copy(tintColor);
  const dark = DARK_TEMPLATE.clone();
  const springMat = SPRING_TEMPLATE.clone();
  springMat.color.copy(tintColor).lerp(new Color(0xffffff), 0.5);
  const materials: MeshLambertMaterial[] = [bone, stick, dark, springMat];

  const root = new Group();
  root.userData['nameTagText'] = null;

  const tagAnchor = new Object3D();
  root.add(tagAnchor);

  // --- skeleton -----------------------------------------------------------
  const body = new Group();
  root.add(body);

  const hips = joint(body, 0, 0, 0);
  addMesh(hips, SHARED_BOX, bone, 0, 0, 0, 0.28, 0.16, 0.18);

  const spine = joint(hips, 0, 0.08, 0);
  addMesh(spine, SHARED_BOX, bone, 0, 0.12, 0, 0.11, 0.24, 0.11);

  const chest = joint(spine, 0, 0.24, 0);
  addMesh(chest, SHARED_BOX, bone, 0, 0.15, 0, 0.22, 0.3, 0.16);
  // Ribs: four tapering bands, the cheapest possible "this is a ribcage" read.
  for (let i = 0; i < 4; i++) {
    const y = 0.05 + i * 0.07;
    const w = 0.33 - i * 0.025;
    addMesh(chest, SHARED_BOX, bone, 0, y, 0, w, 0.03, 0.225 - i * 0.012);
  }

  const neck = joint(chest, 0, 0.32, 0);
  addMesh(neck, SHARED_CYL, bone, 0, 0.05, 0, 0.07, 0.1, 0.07);

  const skull = joint(neck, 0, 0.12, 0);
  addMesh(skull, SHARED_SPHERE, bone, 0, 0.14, 0, 0.26, 0.3, 0.27);
  const jaw = joint(skull, 0, 0.06, -0.03);
  addMesh(jaw, SHARED_BOX, bone, 0, -0.035, -0.03, 0.17, 0.07, 0.14);
  addMesh(skull, SHARED_SPHERE, dark, 0.065, 0.17, -0.115, 0.06, 0.06, 0.06);
  addMesh(skull, SHARED_SPHERE, dark, -0.065, 0.17, -0.115, 0.06, 0.06, 0.06);

  const armL = buildArm(chest, bone, 1);
  const armR = buildArm(chest, bone, -1);
  const legL = buildLeg(hips, bone, 1);
  const legR = buildLeg(hips, bone, -1);

  // --- pogo stick ---------------------------------------------------------
  // Three sections: everything above the spring (sinks on impact), the spring
  // itself (scales), and the tip (stays planted on the ground).
  const stickUpper = new Group();
  root.add(stickUpper);
  addMesh(stickUpper, SHARED_CYL, stick, 0, -0.2, 0, 0.075, 1.06, 0.075);
  const handle = addMesh(stickUpper, SHARED_CYL, stick, 0, 0.28, 0, 0.055, 0.36, 0.055);
  handle.rotation.z = Math.PI / 2;
  addMesh(stickUpper, SHARED_BOX, dark, 0.115, -0.7, 0, 0.14, 0.045, 0.1);
  addMesh(stickUpper, SHARED_BOX, dark, -0.115, -0.7, 0, 0.14, 0.045, 0.1);

  const spring = new Group();
  spring.position.y = RIG.SPRING_TOP;
  root.add(spring);
  addMesh(spring, SHARED_CYL, springMat, 0, -RIG.SPRING_LEN / 2, 0, 0.15, RIG.SPRING_LEN, 0.15);

  const tipGroup = new Group();
  tipGroup.position.y = RIG.SPRING_TOP - RIG.SPRING_LEN;
  root.add(tipGroup);
  addMesh(tipGroup, SHARED_CYL, stick, 0, -0.05, 0, 0.05, 0.1, 0.05);
  // The cone's point must land on the physics contact point, or the stick will
  // visibly float above every surface it bounces off.
  const tipTop = RIG.SPRING_TOP - RIG.SPRING_LEN - 0.1;
  const tipEnd = -(TUNING.STICK_LENGTH + TUNING.TIP_RADIUS);
  const coneLen = tipTop - tipEnd;
  addMesh(tipGroup, SHARED_CONE, dark, 0, -0.1 - coneLen / 2, 0, 0.26, coneLen, 0.26);

  const parts: SkeletonParts = {
    root,
    tagAnchor,
    body,
    hips,
    spine,
    chest,
    neck,
    skull,
    jaw,
    shoulderL: armL.shoulder,
    shoulderR: armR.shoulder,
    elbowL: armL.elbow,
    elbowR: armR.elbow,
    hipL: legL.hip,
    hipR: legR.hip,
    kneeL: legL.knee,
    kneeR: legR.knee,
    ankleL: legL.ankle,
    ankleR: legR.ankle,
    stickUpper,
    spring,
    stickMat: stick,
    tint: tintColor,
    anim: createAnimState(),
  };

  // --- name tag -----------------------------------------------------------
  // Self-contained on purpose: chunk D owns `nametag.ts` and its `GhostManager`
  // may swap this out, but a rig must be able to label itself on its own.
  let tagSprite: Sprite | null = null;
  let tagTexture: Texture | null = null;
  let opacity = 1;

  const clearTag = (): void => {
    if (tagSprite !== null) {
      tagAnchor.remove(tagSprite);
      tagSprite.material.dispose();
      tagSprite = null;
    }
    if (tagTexture !== null) {
      tagTexture.dispose();
      tagTexture = null;
    }
  };

  const setNameTag = (name: string | null): void => {
    clearTag();
    root.userData['nameTagText'] = name;
    if (name === null || name === '') return;

    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;
    ctx.font = 'bold 34px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Outline first so the name survives against both sky and rock.
    ctx.lineWidth = 6;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.strokeText(name, 128, 34);
    ctx.fillStyle = `#${tintColor.getHexString()}`;
    ctx.fillText(name, 128, 34);

    tagTexture = new CanvasTexture(canvas);
    tagTexture.minFilter = LinearFilter;
    tagTexture.magFilter = LinearFilter;
    const mat = new SpriteMaterial({
      map: tagTexture,
      transparent: true,
      depthWrite: false,
      fog: false,
      opacity,
    });
    tagSprite = new Sprite(mat);
    tagSprite.position.y = TAG_HEIGHT;
    tagSprite.scale.set(1.6, 0.4, 1);
    tagAnchor.add(tagSprite);
  };

  return {
    root,
    applyPose(pose: SkeletonPose): void {
      applySkeletonPose(parts, pose);
    },
    setNameTag,
    setOpacity(o: number): void {
      opacity = o < 0 ? 0 : o > 1 ? 1 : o;
      const transparent = opacity < 1;
      for (const m of materials) {
        if (m.transparent !== transparent) {
          m.transparent = transparent;
          m.needsUpdate = true;
        }
        m.opacity = opacity;
        m.depthWrite = !transparent;
      }
      if (tagSprite !== null) tagSprite.material.opacity = opacity;
    },
    dispose(): void {
      clearTag();
      for (const m of materials) m.dispose();
      root.removeFromParent();
      // Shared geometries outlive every rig — disposing one here would blank
      // every other skeleton in the scene.
    },
  };
}
